import React, { useEffect, useMemo, useRef, useState } from 'react';
import './PZDocuments.css';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Tag, App, Input, Select, DatePicker, Row, Col, Card, Modal, Descriptions, Divider, Dropdown, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, EyeOutlined, SearchOutlined, ClearOutlined, FileSearchOutlined, InboxOutlined, MoreOutlined, FilePdfOutlined, ThunderboltOutlined, FormOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useFeedback } from '../utils/feedback';
import { getPZErrorMessage, isBlockingPZError } from '../modules/warehouse/pzErrorMessages';
import { authService } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currency';
import { formatPZDocumentTotal } from '../utils/pzTotals';
import dayjs from 'dayjs';
import { PZDocument, PZItem, PZStatus } from '../types/pz';
import {
  canDeleteWarehouseDocument,
  canManagePZDocuments,
} from '../modules/warehouse/warehouseDocumentAccessPolicy';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import { compareMonthlyDocumentNumbers } from '../utils/documentNumber';
import {
  PZ_OPERATIONAL_STATUSES,
  getPZOperationalStatus,
} from '../modules/warehouse/warehouseDocumentOperationalStatus';
import PZTransportSettlementPanel from '../modules/warehouse/components/PZTransportSettlementPanel';

const { RangePicker } = DatePicker;
const { Option } = Select;

const PZDocuments: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['pz', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [pzDocuments, setPzDocuments] = useState<PZDocument[]>([]);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [downloading, setDownloading] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<PZDocument | null>(null);
  const [createChoiceOpen, setCreateChoiceOpen] = useState(false);
  const [statusChangingId, setStatusChangingId] = useState<string | null>(null);
  const statusChangesInFlightRef = useRef(new Set<string>());

  const currentUser = authService.getCurrentUser();
  const canEdit = canManagePZDocuments(currentUser);
  const invoiceWorkflowEnabled = currentUser?.invoiceWorkflowEnabled === true;
  const canDelete = canDeleteWarehouseDocument(currentUser);
  const canViewPZPurchasePrices = authService.canViewWarehouseDocumentPurchasePrices();
  const canViewPurchaseDocuments = authService.canViewPurchaseDocuments();

  // Filter states
  const [filters, setFilters] = useState<{
    status: PZStatus[];
    dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    search: string;
  }>({
    status: [],
    dateRange: null,
    search: ''
  });

  useEffect(() => {
    fetchPZDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPZDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/pz');
      setPzDocuments(response.data);
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  usePageAutoRefresh(fetchPZDocuments);

  const handleDelete = async (id: string, pzNumber: string) => {
    try {
      await api.delete(`/pz/${id}`);
      feedback.success(t('pz:success.deleted', { pzNumber }));
      fetchPZDocuments();
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.deleteFailed', { pzNumber }), {
        sticky: isBlockingPZError(error),
      });
    }
  };

  const handleStatusUpdate = async (id: string, pzNumber: string, status: PZStatus) => {
    if (statusChangesInFlightRef.current.has(id)) return;
    statusChangesInFlightRef.current.add(id);
    setStatusChangingId(id);
    try {
      await api.put(`/pz/${id}/status`, { status });
      if (status === PZStatus.RECEIVED) {
        feedback.success(t('pz:success.received', { pzNumber }));
      } else if (status === PZStatus.CONFIRMED) {
        feedback.success(t('pz:success.confirmed', { pzNumber }));
      } else if (status === PZStatus.CANCELLED) {
        feedback.success(t('pz:success.cancelled', { pzNumber }));
      }
      await fetchPZDocuments();
    } catch (error: any) {
      // Shared mapper: the same backend code must read the same on the list as
      // it does on the document page.
      const errorKey = status === PZStatus.RECEIVED ? 'receiveFailed'
        : status === PZStatus.CONFIRMED ? 'confirmFailed'
        : status === PZStatus.CANCELLED ? 'cancelFailed' : 'saveFailed';
      feedback.error(getPZErrorMessage(error, t, `pz:error.${errorKey}`), {
        sticky: isBlockingPZError(error),
      });
    } finally {
      statusChangesInFlightRef.current.delete(id);
      setStatusChangingId((current) => current === id ? null : current);
    }
  };

  const confirmStatusUpdate = async (record: PZDocument, status: PZStatus) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('pz:confirm.receivePZ'),
        content: t('pz:confirm.receivePZDescription'),
        okText: t('pz:actions.receiveVirtual'),
        cancelText: t('common:no'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) return;
    await handleStatusUpdate(record.id, record.pzNumber, status);
  };

  const handleDownloadPDF = async (id: string, pzNumber: string) => {
    setDownloading(id);
    try {
      const response = await api.get(`/pz/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${pzNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      feedback.success(t('pz:success.pdfDownloaded', { pzNumber }));
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.pdfGenerateFailed'));
    } finally {
      setDownloading(null);
    }
  };

  const handlePreviewDocument = (doc: PZDocument) => {
    setPreviewDoc(doc);
    setPreviewVisible(true);
  };

  const getStatusColor = (status: PZStatus) => {
    switch (status) {
      case PZStatus.DRAFT:
        return 'default';
      case PZStatus.CONFIRMED:
        return 'processing';
      case PZStatus.RECEIVED:
        return 'success';
      case PZStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  // Filter documents based on current filters
  const filteredDocuments = useMemo(() => {
    let filtered = [...pzDocuments];

    // Status filter
    if (filters.status.length > 0) {
      filtered = filtered.filter(doc => filters.status.includes(getPZOperationalStatus(doc.status)));
    }

    // Date range filter
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const startDate = filters.dateRange[0].startOf('day');
      const endDate = filters.dateRange[1].endOf('day');
      filtered = filtered.filter(doc => {
        const receiptDate = dayjs(doc.receiptDate);
        return receiptDate.isAfter(startDate) && receiptDate.isBefore(endDate);
      });
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.pzNumber.toLowerCase().includes(searchLower) ||
        doc.supplierName?.toLowerCase().includes(searchLower) ||
        doc.supplier?.name?.toLowerCase().includes(searchLower) ||
        (canViewPurchaseDocuments && doc.supplierInvoiceNumber?.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  }, [pzDocuments, filters, canViewPurchaseDocuments]);

  const clearFilters = () => {
    setFilters({
      status: [],
      dateRange: null,
      search: ''
    });
  };

  const getActionMenuItems = (record: PZDocument): MenuProps['items'] => {
    const statusChanging = statusChangingId === record.id;
    const items: NonNullable<MenuProps['items']> = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: t('common:open'),
      },
      {
        key: 'preview',
        icon: <FileSearchOutlined />,
        label: t('pz:actions.preview'),
      },
    ];

    if (canEdit && record.status === PZStatus.DRAFT) {
      items.push(
        {
          key: 'edit',
          icon: <EditOutlined />,
          label: t('common:edit'),
        },
        {
          key: 'receive',
          icon: <InboxOutlined />,
          label: t('pz:actions.receiveVirtual'),
          disabled: statusChanging,
        }
      );
    }

    if (canEdit && record.status === PZStatus.CONFIRMED) {
      items.push({
        key: 'receive',
        icon: <InboxOutlined />,
        label: t('pz:actions.receiveVirtual'),
        disabled: statusChanging,
      });
    }

    if (canViewPZPurchasePrices) {
      items.push({
        key: 'pdf',
        icon: <FilePdfOutlined />,
        label: t('pz:actions.downloadPDF'),
      });
    }

    // Parity with WZ: only a received document (stock already affected) is protected.
    if (canDelete && record.status !== PZStatus.RECEIVED) {
      items.push({
        key: 'delete',
        danger: true,
        icon: <DeleteOutlined />,
        label: t('common:delete'),
      });
    }

    return items;
  };

  const columns = [
    {
      title: t('pz:fields.pzNumber'),
      dataIndex: 'pzNumber',
      key: 'pzNumber',
      ellipsis: true,
      width: 180,
      sorter: (a: PZDocument, b: PZDocument, sortOrder?: 'ascend' | 'descend' | null) =>
        compareMonthlyDocumentNumbers(a.pzNumber, b.pzNumber, sortOrder),
      defaultSortOrder: 'descend' as const,
      render: (_: unknown, record: PZDocument) => {
        const operationalStatus = record.status ? getPZOperationalStatus(record.status) : null;

        return (
          <div className="pz-documents-page__number-cell">
            <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/app/pz/${record.id}`)}>
              {record.pzNumber || '-'}
            </Button>
            {operationalStatus && (
              <Tag
                color={getStatusColor(operationalStatus)}
                className="pz-documents-page__status"
              >
                {t(`pz:status.${operationalStatus.toLowerCase()}`)}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: t('pz:fields.receiptDate'),
      dataIndex: 'receiptDate',
      key: 'receiptDate',
      render: (date: string) => formatDate(date),
      sorter: (a: PZDocument, b: PZDocument) => dayjs(a.receiptDate).unix() - dayjs(b.receiptDate).unix(),
    },
    {
      title: t('pz:fields.supplier'),
      key: 'supplier',
      render: (record: PZDocument) => (
        <div>
          <div>{record.supplier?.name || record.supplierName}</div>
          {canViewPurchaseDocuments && record.supplierInvoiceNumber && (
            <div style={{ fontSize: '0.9em', color: '#666' }}>
              {t('pz:fields.supplierInvoiceNumber')}: {record.supplierInvoiceNumber}
            </div>
          )}
        </div>
      ),
    },
    ...(canViewPZPurchasePrices ? [{
      title: t('pz:fields.supplierInvoiceAmount'),
      key: 'supplierInvoiceAmount',
      render: (record: PZDocument) =>
        record.supplierInvoiceAmount
          ? formatCurrency(record.supplierInvoiceAmount, record.supplierInvoiceCurrency || 'PLN')
          : '-',
    }] : []),
    {
      title: t('pz:fields.items'),
      key: 'items',
      render: (record: PZDocument) => {
        if (!record.items || record.items.length === 0) return '-';
        const itemNames = record.items.map(item => item.productName || item.sku || 'N/A');
        if (itemNames.length <= 2) {
          return itemNames.join(', ');
        }
        return (
          <span title={itemNames.join(', ')}>
            {itemNames.slice(0, 2).join(', ')} +{itemNames.length - 2}
          </span>
        );
      },
    },
    ...(canViewPZPurchasePrices ? [{
      title: t('pz:fields.totalValue'),
      key: 'totalValue',
      render: (record: PZDocument) => formatPZDocumentTotal(record),
    }] : []),
    {
      title: t('common:actions'),
      key: 'actions',
      width: 80,
      align: 'right' as const,
      fixed: 'right' as const,
      render: (record: PZDocument) => (
        <Dropdown
          trigger={['click']}
          placement="bottomRight"
          menu={{
            items: getActionMenuItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();

              if (key === 'view') {
                navigate(`/app/pz/${record.id}`);
              }

              if (key === 'preview') {
                handlePreviewDocument(record);
              }

              if (key === 'edit') {
                navigate(`/app/pz/${record.id}?mode=edit`);
              }

              if (key === 'pdf') {
                void handleDownloadPDF(record.id, record.pzNumber);
              }

              if (key === 'receive') {
                void confirmStatusUpdate(record, PZStatus.RECEIVED);
              }

              if (key === 'delete') {
                modal.confirm({
                  title: t('pz:confirm.delete'),
                  okText: t('common:yes'),
                  cancelText: t('common:no'),
                  okButtonProps: { danger: true },
                  onOk: () => { void handleDelete(record.id, record.pzNumber); },
                });
              }
            },
          }}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            aria-label={t('common:actions')}
            loading={statusChangingId === record.id}
            disabled={statusChangingId === record.id}
            onClick={(event) => event.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="pz-documents-page">
      <div className="pz-documents-page__header">
        <div>
          <h1 style={{ margin: 0 }}>{t('pz:title')}</h1>
          <p style={{ color: '#666', margin: 0 }}>{t('pz:subtitle')}</p>
        </div>
        {canEdit && (!invoiceWorkflowEnabled || authService.canManageInvoiceImports()) && (
          <Button
            className="pz-documents-page__create-button"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => invoiceWorkflowEnabled ? navigate('/app/purchase-invoices/new') : setCreateChoiceOpen(true)}
          >
            {t('pz:actions.create')}
          </Button>
        )}
      </div>

      {!invoiceWorkflowEnabled && <PZTransportSettlementPanel canCreate={canEdit} />}

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder={t('pz:filters.search')}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              mode="multiple"
              placeholder={t('pz:filters.status')}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: '100%' }}
              allowClear
            >
              {PZ_OPERATIONAL_STATUSES.map(status => (
                <Option key={status} value={status}>
                  {t(`pz:status.${status?.toLowerCase() || 'draft'}`)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
              placeholder={[t('pz:filters.dateFrom'), t('pz:filters.dateTo')]}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              icon={<ClearOutlined />}
              onClick={clearFilters}
              style={{ width: '100%' }}
            >
              {t('common:clearFilters')}
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        className="pz-documents-page__table"
        columns={columns}
        dataSource={filteredDocuments}
        rowKey="id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showTotal: (total) => t('common:totalItems', { count: total }),
        }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={t('pz:newMode.title')}
        open={createChoiceOpen}
        onCancel={() => setCreateChoiceOpen(false)}
        footer={null}
        width={680}
        keyboard
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {t('pz:subtitle')}
        </Typography.Paragraph>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Card
              hoverable
              onClick={() => {
                setCreateChoiceOpen(false);
                navigate('/app/pz/new?creationMode=supplierOrder');
              }}
              style={{ height: '100%', borderColor: '#1677ff' }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
                <ThunderboltOutlined style={{ fontSize: 36, color: '#1677ff' }} />
                <Typography.Title level={5} style={{ marginBottom: 0 }}>
                  {t('pz:newMode.fromTransportTitle')}
                </Typography.Title>
                <Typography.Text type="secondary">
                  {t('pz:newMode.fromTransportDescription')}
                </Typography.Text>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card
              hoverable
              onClick={() => {
                setCreateChoiceOpen(false);
                navigate('/app/pz/new?creationMode=manual');
              }}
              style={{ height: '100%' }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
                <FormOutlined style={{ fontSize: 36, color: '#8c8c8c' }} />
                <Typography.Title level={5} style={{ marginBottom: 0 }}>
                  {t('pz:newMode.manualTitle')}
                </Typography.Title>
                <Typography.Text type="secondary">
                  {t('pz:newMode.manualDescription')}
                </Typography.Text>
              </Space>
            </Card>
          </Col>
        </Row>
      </Modal>

      <Modal
        title={t('pz:actions.documentPreview')}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        maskClosable={false}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            {t('common:close')}
          </Button>,
          ...(canViewPZPurchasePrices && previewDoc ? [
            <Button
              key="download"
              type="primary"
              icon={<FilePdfOutlined />}
              loading={downloading === previewDoc.id}
              onClick={() => handleDownloadPDF(previewDoc.id, previewDoc.pzNumber)}
            >
              {t('pz:actions.downloadPDF')}
            </Button>,
          ] : []),
        ]}
        width={800}
      >
        {previewDoc && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label={t('pz:fields.pzNumber')}>
                {previewDoc.pzNumber}
              </Descriptions.Item>
              <Descriptions.Item label={t('pz:fields.status')}>
                <Tag color={getStatusColor(getPZOperationalStatus(previewDoc.status))}>
                  {t(`pz:status.${getPZOperationalStatus(previewDoc.status).toLowerCase()}`)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('pz:fields.receiptDate')}>
                {formatDate(previewDoc.receiptDate)}
              </Descriptions.Item>
              <Descriptions.Item label={t('pz:fields.supplier')}>
                {previewDoc.supplier?.name || previewDoc.supplierName}
              </Descriptions.Item>
              {canViewPurchaseDocuments && (
                <Descriptions.Item label={t('pz:fields.supplierInvoiceNumber')}>
                  {previewDoc.supplierInvoiceNumber || '-'}
                </Descriptions.Item>
              )}
              {canViewPZPurchasePrices && (
                <>
                  <Descriptions.Item label={t('pz:fields.supplierInvoiceAmount')}>
                    {previewDoc.supplierInvoiceAmount
                      ? formatCurrency(previewDoc.supplierInvoiceAmount, previewDoc.supplierInvoiceCurrency || 'PLN')
                      : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('pz:fields.totalValue')}>
                    {formatPZDocumentTotal(previewDoc)}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>

            {previewDoc.items && previewDoc.items.length > 0 && (
              <>
                <Divider orientation="left">{t('pz:fields.items')}</Divider>
                <Table
                  dataSource={previewDoc.items}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: t('pz:itemFields.productName'),
                      dataIndex: 'productName',
                      key: 'productName',
                      render: (text: string | undefined, record: PZItem) => text || record.sku || '-'
                    },
                    {
                      title: t('pz:itemFields.sku'),
                      dataIndex: 'sku',
                      key: 'sku',
                    },
                    {
                      title: t('pz:itemFields.quantityOrdered'),
                      dataIndex: 'quantityOrdered',
                      key: 'quantityOrdered',
                    },
                    {
                      title: t('pz:itemFields.quantityReceived'),
                      dataIndex: 'quantityReceived',
                      key: 'quantityReceived',
                    },
                    ...(canViewPZPurchasePrices ? [{
                      title: t('pz:itemFields.unitPrice'),
                      dataIndex: 'unitPrice',
                      key: 'unitPrice',
                      render: (value: number | string | undefined, record: PZItem) =>
                        value === undefined ? '-' : formatCurrency(Number(value), record.currency)
                    },
                    {
                      title: t('pz:itemFields.totalPrice'),
                      dataIndex: 'totalPrice',
                      key: 'totalPrice',
                      render: (value: number | string | undefined, record: PZItem) =>
                        value === undefined ? '-' : formatCurrency(Number(value), record.currency)
                    }] : []),
                  ]}
                />
              </>
            )}

            {previewDoc.notes && (
              <>
                <Divider orientation="left">{t('pz:fields.notes')}</Divider>
                <p>{previewDoc.notes}</p>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PZDocuments;
